import { createRng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

/**
 * A car race that is a decision rather than a reflex.
 *
 * Paddle Pong and Slalom Racer are not built because steering needs twenty-odd
 * messages a second, which this server has no way to carry. Racing does not
 * actually require that: pick a gear each turn, and a higher one covers more
 * ground but is more likely to lose you the back end on a corner. Push on the
 * straights, ease off before the bends — which is what driving fast is, and it
 * fits in one message a turn.
 */

export const TRACK_LENGTH = 36;
/** Where the bends are. Everything between them is straight. */
export const CORNERS = [7, 14, 21, 28] as const;
export const TURBO_MOVE_LIMIT = 80;

/** How far each gear carries you, and how likely it is to bite on a corner. */
export const GEARS = [
	{ gear: 1, moves: 2, spinInSix: 0, label: "Steady" },
	{ gear: 2, moves: 4, spinInSix: 2, label: "Quick" },
	{ gear: 3, moves: 6, spinInSix: 4, label: "Flat out" },
] as const;

export interface TurboTrackState extends BaseState {
	kind: "turbotrack";
	at: [number, number];
	/**
	 * How far past the flag each car would have carried on, once it has crossed.
	 * `at` is clamped to the track, so this is what settles a photo finish: when
	 * both cars cross, the one that went further past the line wins.
	 */
	past: [number, number];
	/** Set when a spin costs you your next turn. */
	spinning: [boolean, boolean];
	/**
	 * How many times each car has spun this race. A photo finish where both
	 * cars carried equally far past the flag goes to the smoother driver, so
	 * an identical mirror race can still crown somebody.
	 */
	spins: [number, number];
	/**
	 * Fixes the luck of the whole race up front, so `apply` stays a pure
	 * function of the state. Redacted — holding it would tell you exactly which
	 * corners you can take flat out.
	 */
	rollSeed: number;
	rollsUsed: number;
	movesMade: number;
	lastNote: string;
}

export interface TurboTrackMove {
	gear: number;
}

/** The corners strictly between where you are and where you would end up. */
export function cornersCrossed(from: number, to: number): number {
	return CORNERS.filter((corner) => corner > from && corner <= to).length;
}

export const turboTrack: GameDefinition<TurboTrackState, TurboTrackMove> = {
	id: "turbotrack",
	category: "racing",
	title: "Turbo Track",
	emoji: "🏎️",
	blurb: "Pick your gear. Fast wins races — until you hit a bend too quickly.",
	howTo: [
		"Choose a gear each turn. A higher gear moves you further.",
		"Take a bend too fast and you spin, losing your next turn.",
		"If the car that moved first crosses the flag, the other car gets one last turn.",
		"If you both cross, whoever went further past the flag wins — still level, and the smoother driver (fewer spins) takes it.",
	],

	create(rng) {
		return {
			kind: "turbotrack",
			at: [0, 0],
			past: [0, 0],
			spinning: [false, false],
			spins: [0, 0],
			rollSeed: rng.int(0x7fffffff),
			rollsUsed: 0,
			movesMade: 0,
			lastNote: "Lights out!",
			// The pole position is worth roughly a whole turn, so it is drawn by
			// lot rather than handed to whoever happens to sit in seat 0.
			turn: rng.int(2) as PlayerIndex,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const gear = (raw as { gear?: unknown }).gear;
		if (typeof gear !== "number" || !Number.isInteger(gear)) return null;
		return GEARS.some((option) => option.gear === gear) ? { gear } : null;
	},

	apply(state, player, move): MoveOutcome<TurboTrackState> {
		if (state.result) return reject("This race is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const them = otherPlayer(player);
		const spinning: [boolean, boolean] = [...state.spinning];

		// Still gathering it up after a spin: this turn is spent doing that.
		if (spinning[player]) {
			spinning[player] = false;
			// A recovery spent as the one answering turn is an answer of nought.
			if (state.at[them] >= TRACK_LENGTH) {
				return {
					ok: true,
					state: {
						...state,
						spinning,
						turn: null,
						movesMade: state.movesMade + 1,
						lastNote: "Still spinning as the flag falls!",
						result: { winner: them },
					},
				};
			}
			return {
				ok: true,
				state: {
					...state,
					spinning,
					turn: them,
					movesMade: state.movesMade + 1,
					lastNote: "Back on the track.",
				},
			};
		}

		const option = GEARS.find((entry) => entry.gear === move.gear);
		if (!option) return reject("That isn't a gear.");

		const at: [number, number] = [...state.at];
		const past: [number, number] = [...state.past];
		const reach = at[player] + option.moves;
		const target = Math.min(TRACK_LENGTH, reach);
		const bends = cornersCrossed(at[player], target);

		// One roll per bend crossed, so a long gear through two of them is twice
		// the gamble — which is exactly the choice being offered.
		let rollsUsed = state.rollsUsed;
		let spun = false;
		let stoppedAt = target;
		for (const corner of CORNERS) {
			if (corner <= at[player] || corner > target) continue;
			const roll = createRng((state.rollSeed + rollsUsed * 15_485_863) >>> 0).int(6);
			rollsUsed += 1;
			if (roll < option.spinInSix) {
				spun = true;
				stoppedAt = corner;
				break;
			}
		}

		at[player] = stoppedAt;
		if (stoppedAt >= TRACK_LENGTH) past[player] = reach - TRACK_LENGTH;
		spinning[player] = spun;
		const spins: [number, number] = [...state.spins];
		if (spun) spins[player] += 1;
		const movesMade = state.movesMade + 1;
		const next: TurboTrackState = {
			...state,
			at,
			past,
			spinning,
			spins,
			rollsUsed,
			movesMade,
			scores: at,
			lastNote: spun
				? "Spun at the bend! 💨"
				: bends > 0
					? `Through the bend in ${option.label.toLowerCase()}.`
					: `${option.label} down the straight.`,
		};

		/*
		 * Crossing the flag ends the race unless the other car is a whole turn
		 * behind. Turns alternate, so under equal play whoever moved first would
		 * always cross first — a race decided by seat, not by driving. The car
		 * that moved second therefore gets exactly one answering turn (turns
		 * alternate strictly, so the opener's moves are the odd-numbered ones,
		 * whichever seat drew the pole), and a photo finish goes to whoever
		 * carried further past the line.
		 */
		if (stoppedAt >= TRACK_LENGTH) {
			if (at[them] >= TRACK_LENGTH) {
				// The answer came: both cars are over the line. An exact photo
				// finish goes to whoever kept the car pointing forwards — only a
				// race level on distance *and* spins is a dead heat.
				const winner =
					past[player] !== past[them]
						? past[player] > past[them]
							? player
							: them
						: spins[player] !== spins[them]
							? spins[player] < spins[them]
								? player
								: them
							: null;
				return { ok: true, state: { ...next, turn: null, result: { winner } } };
			}
			if (movesMade % 2 === 1) {
				return {
					ok: true,
					state: { ...next, turn: them, lastNote: "Over the line — one turn to answer!" },
				};
			}
			return { ok: true, state: { ...next, turn: null, result: { winner: player } } };
		}
		if (at[them] >= TRACK_LENGTH) {
			// The answering turn fell short of the line, so the race is over.
			return { ok: true, state: { ...next, turn: null, result: { winner: them } } };
		}
		if (movesMade >= TURBO_MOVE_LIMIT) {
			const winner = at[0] === at[1] ? null : at[0] > at[1] ? 0 : 1;
			return { ok: true, state: { ...next, turn: null, result: { winner } } };
		}
		return { ok: true, state: { ...next, turn: them } };
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		return GEARS.map((option) => ({ gear: option.gear }));
	},

	/** The seed is the luck of the whole race, so it stays on the server. */
	redact(state) {
		return { ...state, rollSeed: 0 };
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};

/**
 * Expected ground gained, gear by gear, counting a spin as the distance lost
 * plus the turn lost. Which reproduces the thing a driver knows without doing
 * any arithmetic: go hard on the straights, lift before the bends.
 */
export function bestGear(state: TurboTrackState, player: PlayerIndex): TurboTrackMove | null {
	let best: TurboTrackMove | null = null;
	let bestValue = Number.NEGATIVE_INFINITY;

	for (const option of GEARS) {
		const target = Math.min(TRACK_LENGTH, state.at[player] + option.moves);
		const bends = cornersCrossed(state.at[player], target);
		const safe = (1 - option.spinInSix / 6) ** bends;
		const gained = target - state.at[player];
		// A spin stops you at the bend and costs the next turn, so the downside
		// is roughly the ground you would have made twice over.
		const value = safe * gained - (1 - safe) * gained;

		if (value > bestValue) {
			bestValue = value;
			best = { gear: option.gear };
		}
	}
	return best;
}
